import React from "react";
import { Link } from "react-router-dom";
import BrandMark from "./BrandMark";
import tokens from "../tokens.json";

/**
 * Mission Dresseur — Logo system (canonical lockups).
 *
 * Design spec (Brand Guidelines doc):
 *   - The mark is the visual pillar — slightly taller than the text block.
 *   - Title in Inter Bold (700) sized to read as the primary signal.
 *   - Tagline in Inter Regular (400), smaller, generous tracking.
 *   - Vertical: total text height ≈ 70-80 % of icon height H.
 *   - Horizontal gap E ≈ 0.6 × H between icon and text.
 *   - Title baseline + tagline baseline are calibrated so the icon's
 *     visual centre aligns with the gap between the two text lines.
 *
 * Sizes are semantic (sm | md | lg | xl). All other consumers must use
 * these — bespoke logo lockups are forbidden.
 */

// Precise per-size geometry — H = icon height, E = gap, T = title fontsize,
// L = tagline fontsize, LH_T/LH_L = line-heights, GAP_TL = gap title→tagline.
// (Values verified visually at navbar / favicon / OG / PDF scales.)
const SIZE = {
    sm: { H: 32,  E: 19, T: 18,  L: 9,  LH_T: 18 * 1.0, LH_L: 9  * 1.0, GAP_TL: 3, TAGLINE_LETTER_SPACING: "0.20em", TITLE_LETTER_SPACING: "-0.018em" },
    md: { H: 44,  E: 26, T: 22,  L: 10, LH_T: 22 * 1.0, LH_L: 10 * 1.0, GAP_TL: 4, TAGLINE_LETTER_SPACING: "0.22em", TITLE_LETTER_SPACING: "-0.020em" },
    lg: { H: 64,  E: 38, T: 32,  L: 12, LH_T: 32 * 1.0, LH_L: 12 * 1.0, GAP_TL: 5, TAGLINE_LETTER_SPACING: "0.24em", TITLE_LETTER_SPACING: "-0.022em" },
    xl: { H: 88,  E: 52, T: 44,  L: 14, LH_T: 44 * 1.0, LH_L: 14 * 1.0, GAP_TL: 6, TAGLINE_LETTER_SPACING: "0.26em", TITLE_LETTER_SPACING: "-0.024em" },
};

export function BrandLogo({
    variant = "horizontal",
    size = "md",
    tone = "ink",        // ink | cream | mono | print
    showTagline = true,
    tagline,
    to = "/",
    className = "",
    testId = "brand-logo",
}) {
    const s = SIZE[size] || SIZE.md;
    const taglineText = tagline ?? tokens.brand.tagline;

    // Colour resolution (driven by tone + theme tokens).
    const titleColor = tone === "print" ? "#0B1021"
                     : tone === "cream" ? "var(--md-primary-fg)"
                     : "var(--md-text)";
    const tagColor   = tone === "print" ? "#6B7280"
                     : tone === "cream" ? "color-mix(in srgb, var(--md-primary-fg) 65%, transparent)"
                     : "var(--md-text-2)";

    // The wordmark block — height calibrated to stay < H (~70-80 %).
    const blockHeight = (showTagline && variant === "horizontal")
        ? s.LH_T + s.GAP_TL + s.LH_L
        : s.LH_T;

    const wordmark = (
        <span
            className="flex flex-col"
            style={{
                height: blockHeight,
                justifyContent: "space-between",
            }}
        >
            <span
                style={{
                    fontFamily: "Inter, system-ui, sans-serif",
                    fontWeight: 700,                 // Inter Bold (Bold/SemiBold mid-weight per spec)
                    fontSize: `${s.T}px`,
                    lineHeight: 1,
                    letterSpacing: s.TITLE_LETTER_SPACING,
                    color: titleColor,
                    fontFeatureSettings: '"cv11", "ss01", "ss03"',
                }}
            >
                {tokens.brand.name}
            </span>
            {showTagline && variant === "horizontal" && (
                <span
                    style={{
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontWeight: 400,             // Inter Regular per spec
                        fontSize: `${s.L}px`,
                        lineHeight: 1,
                        letterSpacing: s.TAGLINE_LETTER_SPACING,
                        textTransform: "uppercase",
                        color: tagColor,
                    }}
                >
                    {taglineText}
                </span>
            )}
        </span>
    );

    const compactWord = (
        <span
            className="flex flex-col"
            style={{
                height: s.H * 0.78,
                justifyContent: "center",
                lineHeight: 0.92,
                fontFamily: "Inter, system-ui, sans-serif",
                fontWeight: 700,
                letterSpacing: s.TITLE_LETTER_SPACING,
            }}
        >
            <span style={{ fontSize: `${s.T * 0.85}px`, color: titleColor }}>Mission</span>
            <span style={{ fontSize: `${s.T * 0.85}px`, color: tone === "cream" ? "var(--md-secondary)" : "var(--md-secondary)" }}>Dresseur</span>
        </span>
    );

    const content = (() => {
        if (variant === "icon")     return <BrandMark tone={tone} size={s.H} title={tokens.brand.name} />;
        if (variant === "wordmark") return wordmark;
        if (variant === "compact") {
            return (
                <span className="inline-flex items-center" style={{ gap: `${s.E * 0.62}px` }}>
                    <BrandMark tone={tone} size={s.H} title={tokens.brand.name} />
                    {compactWord}
                </span>
            );
        }
        if (variant === "print") {
            return (
                <span className="inline-flex items-center" style={{ gap: `${s.E}px`, color: "#0B1021" }}>
                    <BrandMark tone="print" size={s.H} title={tokens.brand.name} />
                    {wordmark}
                </span>
            );
        }
        // horizontal (default)
        return (
            <span className="inline-flex items-center" style={{ gap: `${s.E}px` }}>
                <BrandMark tone={tone} size={s.H} title={tokens.brand.name} />
                {wordmark}
            </span>
        );
    })();

    const inner = (
        <span className={`inline-flex items-center ${className}`} data-testid={testId}>
            {content}
        </span>
    );

    if (to === null) return inner;
    return (
        <Link to={to} className="inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-md-secondary/40">
            {inner}
        </Link>
    );
}

export { BrandMark } from "./BrandMark";
export default BrandLogo;
